import React, { useState, useEffect } from "react";
import {
  Box,
  Chip,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  Stack,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import {
  useLazyListShipmentPricesForReportsQuery,
  useDownloadDeliveryCollectionReportExcelMutation,
} from "store/services/reportApi";
import { useLazyGetDDLContractorListByShipmentStatusQuery } from "store/services/contractorApi";
import HighlightedText from "components/Shared/HighlightedText";
import BaseSelect from "components/Shared/Base/BaseSelect";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyGetDDLDriverListByContractorQuery } from "store/services/driverApi";
import { Download } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addDays, differenceInDays } from "date-fns";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetDdlRegionListQuery } from "store/services/LocationsApi/regionApi";
import { useLazyGetBranchByRegionDDLQuery } from "store/services/branchApi";

const getPaymentStatusStyle = (status) => {
  const styles = {
    Paid: { backgroundColor: "#DFF7DF", textColor: "green" },
    "Not Paid": { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  };
  return styles[status] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const DeliveryCollectionReport = () => {
  const { isVendor, vendorId, viewEntity } = useAuth();
  const { reset } = useForm({
    defaultValues: {
      selectedPrices: [],
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const keyword = searchParams.get("search") || "";
  const [sortBy, setSortBy] = useState("updatedAt:-1");
  const [timeRange, setTimeRange] = useState("all");
  const [paymentStatus, setPaymentStatus] = useState(
    searchParams.get("paymentStatus") || "Not Paid"
  );
  const [contractorId, setContractorId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);

  // New state for branch/region filters
  const [sourceBranch, setSourceBranch] = useState(
    searchParams.get("sourceBranch")?.split(",") || []
  );
  const [destinationBranch, setDestinationBranch] = useState(
    searchParams.get("destinationBranch")?.split(",") || []
  );
  const [sourceRegion, setSourceRegion] = useState(
    searchParams.get("sourceRegion") || ""
  );
  const [destinationRegion, setDestinationRegion] = useState(
    searchParams.get("destinationRegion") || ""
  );

  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "COD Transaction Report" },
  ];

  const [
    fetchShipmentPrices,
    { data: priceRes, isLoading, isFetching, error },
  ] = useLazyListShipmentPricesForReportsQuery();
  const [
    fetchContractors,
    { data: contractors, isLoading: loadingContractors },
  ] = useLazyGetDDLContractorListByShipmentStatusQuery();
  const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] =
    useLazyGetDDLDriverListByContractorQuery();
  const [downloadExcel, { isLoading: isDownloading }] =
    useDownloadDeliveryCollectionReportExcelMutation();

  // New queries for branches and regions
  const [fetchBranches, { data: branchesData, isLoading: isBranchesLoading }] =
    useLazyGetDDLBranchListQuery();
  const [fetchRegions, { data: regionsData }] = useLazyGetDdlRegionListQuery();
  const [fetchSourceBranchesByRegion, { data: sourceBranchesData }] =
    useLazyGetBranchByRegionDDLQuery();
  const [fetchDestinationBranchesByRegion, { data: destinationBranchesData }] =
    useLazyGetBranchByRegionDDLQuery();

  // Mapped data for branches and regions
  const mappedBranchList = React.useMemo(() => {
    const dataArray = Array.isArray(branchesData)
      ? branchesData
      : Array.isArray(branchesData?.data)
        ? branchesData.data
        : [];

    return dataArray
      .filter((item) => item?._id && item?.name && item?.code)
      .map((item) => ({
        label: `${item.name} - ${item.code}`,
        value: item._id,
      }));
  }, [branchesData]);

  const mappedRegionList = React.useMemo(() => {
    const dataArray = Array.isArray(regionsData)
      ? regionsData
      : Array.isArray(regionsData?.data)
        ? regionsData.data
        : [];

    return dataArray
      .filter((item) => item?._id && item?.name)
      .map((item) => ({
        label: item.name,
        value: item._id,
      }));
  }, [regionsData]);

  const mappedSourceRegionBranches = React.useMemo(
    () =>
      Array.isArray(sourceBranchesData?.data)
        ? sourceBranchesData.data
          .filter((item) => item?._id && item?.name && item?.code)
          .map((item) => ({
            label: `${item.name} - ${item.code}`,
            value: item._id,
          }))
        : [],
    [sourceBranchesData]
  );

  const mappedDestinationRegionBranches = React.useMemo(
    () =>
      Array.isArray(destinationBranchesData?.data)
        ? destinationBranchesData.data
          .filter((item) => item?._id && item?.name && item?.code)
          .map((item) => ({
            label: `${item.name} - ${item.code}`,
            value: item._id,
          }))
        : [],
    [destinationBranchesData]
  );

  const selectedSource = React.useMemo(
    () =>
      mappedBranchList?.filter((branch) => sourceBranch.includes(branch.value)),
    [mappedBranchList, sourceBranch]
  );

  const selectedDestination = React.useMemo(
    () =>
      mappedBranchList?.filter((branch) =>
        destinationBranch.includes(branch.value)
      ),
    [mappedBranchList, destinationBranch]
  );

  const selectedSourceRegion = React.useMemo(
    () => mappedRegionList?.find((region) => region.value === sourceRegion),
    [mappedRegionList, sourceRegion]
  );

  const selectedDestinationRegion = React.useMemo(
    () =>
      mappedRegionList?.find((region) => region.value === destinationRegion),
    [mappedRegionList, destinationRegion]
  );

  const sourceBranchIdsFromRegion = React.useMemo(
    () => mappedSourceRegionBranches.map((branch) => branch.value),
    [mappedSourceRegionBranches]
  );

  const destinationBranchIdsFromRegion = React.useMemo(
    () => mappedDestinationRegionBranches.map((branch) => branch.value),
    [mappedDestinationRegionBranches]
  );

  const handleFetchRegions = () => {
    if (viewEntity?.entity?.account?._id && viewEntity?.entity?._id) {
      fetchRegions({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      })
        .unwrap()
        .catch((error) => {
          console.error("Failed to fetch regions:", error);
        });
    }
  };

  const handleSourceRegionChange = (_, selectedRegion) => {
    const regionId = selectedRegion?.value || "";
    setSourceRegion(regionId);
    setSourceBranch([]);

    if (
      regionId &&
      viewEntity?.entity?.account?._id &&
      viewEntity?.entity?._id
    ) {
      fetchSourceBranchesByRegion({
        regionId,
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      })
        .unwrap()
        .catch((error) => {
          console.error("Failed to fetch source region branches:", error);
        });
    }
  };

  const handleDestinationRegionChange = (_, selectedRegion) => {
    const regionId = selectedRegion?.value || "";
    setDestinationRegion(regionId);
    setDestinationBranch([]);

    if (
      regionId &&
      viewEntity?.entity?.account?._id &&
      viewEntity?.entity?._id
    ) {
      fetchDestinationBranchesByRegion({
        regionId,
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      })
        .unwrap()
        .catch((error) => {
          console.error("Failed to fetch destination region branches:", error);
        });
    }
  };

  // Calculate fromDate and toDate based on timeRange or custom dates
  const getDateRange = (range, from, to) => {
    const today = moment().startOf("day");
    switch (range) {
      case "today":
        return {
          fromDate: today.format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "last7days":
        return {
          fromDate: moment()
            .subtract(6, "days")
            .startOf("day")
            .format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "oneMonth":
        return {
          fromDate: moment()
            .subtract(1, "month")
            .startOf("day")
            .format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "custom":
        if (
          from &&
          to &&
          differenceInDays(new Date(to), new Date(from)) <= 90
        ) {
          return {
            fromDate: moment(from).format("MM-DD-YYYY"),
            toDate: moment(to).format("MM-DD-YYYY"),
          };
        }
        setErrorMessage("Date range cannot exceed 90 days.");
        return { fromDate: null, toDate: null };
      case "all":
      default:
        return { fromDate: null, toDate: null };
    }
  };

  useEffect(() => {
    // Fetch contractors and branches on mount
    fetchContractors({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      active: true,
    });
    fetchBranches({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
    });
  }, [fetchContractors, fetchBranches, viewEntity]);

  useEffect(() => {
    // Fetch drivers when contractorId changes
    if (contractorId) {
      fetchDrivers({ contractorId });
      setDriverId("");
    } else {
      setDriverId("");
    }
  }, [contractorId, fetchDrivers]);

  useEffect(() => {
    // Set default paymentStatus to "Not Paid" if no searchParams
    if (!searchParams.get("paymentStatus")) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("paymentStatus", "Not Paid");
      setSearchParams(newParams, { replace: true });
    }

    const [sortedBy, sortDirection] = sortBy.split(":");
    const { fromDate, toDate } = getDateRange(
      timeRange,
      customFromDate,
      customToDate
    );

    // Update searchParams with all filters
    const newParams = new URLSearchParams(searchParams);
    if (fromDate) newParams.set("fromDate", fromDate);
    else newParams.delete("fromDate");
    if (toDate) newParams.set("toDate", toDate);
    else newParams.delete("toDate");
    if (paymentStatus !== searchParams.get("paymentStatus")) {
      newParams.set("paymentStatus", paymentStatus);
    }
    if (contractorId) newParams.set("contractorId", contractorId);
    else newParams.delete("contractorId");
    if (driverId) newParams.set("driverId", driverId);
    else newParams.delete("driverId");

    // Update params for new branch/region filters
    if (sourceBranch.length > 0) {
      newParams.set("sourceBranch", sourceBranch.join(","));
    } else {
      newParams.delete("sourceBranch");
    }
    if (destinationBranch.length > 0) {
      newParams.set("destinationBranch", destinationBranch.join(","));
    } else {
      newParams.delete("destinationBranch");
    }
    if (sourceRegion) {
      newParams.set("sourceRegion", sourceRegion);
    } else {
      newParams.delete("sourceRegion");
    }
    if (destinationRegion) {
      newParams.set("destinationRegion", destinationRegion);
    } else {
      newParams.delete("destinationRegion");
    }

    setSearchParams(newParams, { replace: true });

    // Determine final branch IDs
    const finalSourceBranchIds = sourceRegion
      ? sourceBranchIdsFromRegion
      : sourceBranch;
    const finalDestinationBranchIds = destinationRegion
      ? destinationBranchIdsFromRegion
      : destinationBranch;

    const apiParams = {
      chargeType: ["Charge", "Cash on Delivery"],
      paymentLocation: "Destination",
      status: "Delivered",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      ...(isVendor && { vendorId }),
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      sortedBy,
      sortDirection,
      ...(paymentStatus !== "all" && { paymentStatus }),
      ...(contractorId && { contractorId }),
      ...(driverId && { driverId }),
      ...(finalSourceBranchIds?.length && {
        sourceBranchId: finalSourceBranchIds,
      }),
      ...(finalDestinationBranchIds?.length && {
        destinationBranchId: finalDestinationBranchIds,
      }),
      reportType: "delivery",
    };

    fetchShipmentPrices(apiParams);
  }, [
    fetchShipmentPrices,
    currentPage,
    perPage,
    keyword,
    isVendor,
    vendorId,
    viewEntity,
    timeRange,
    sortBy,
    paymentStatus,
    contractorId,
    driverId,
    customFromDate,
    customToDate,
    sourceBranch,
    destinationBranch,
    sourceRegion,
    destinationRegion,
    sourceBranchIdsFromRegion,
    destinationBranchIdsFromRegion,
    setSearchParams,
    searchParams,
  ]);

  const clearAllFilters = () => {
    setSearchParams({ paymentStatus: "Not Paid" });
    setTimeRange("all");
    setPaymentStatus("Not Paid");
    setContractorId("");
    setDriverId("");
    setCustomFromDate(null);
    setCustomToDate(null);
    setSourceBranch([]);
    setDestinationBranch([]);
    setSourceRegion("");
    setDestinationRegion("");
    reset({ selectedPrices: [] });
  };

  const handleDownloadExcel = async () => {
    const [sortedBy, sortDirection] = sortBy.split(":");
    const { fromDate, toDate } = getDateRange(
      timeRange,
      customFromDate,
      customToDate
    );

    const finalSourceBranchIds = sourceRegion
      ? sourceBranchIdsFromRegion
      : sourceBranch;
    const finalDestinationBranchIds = destinationRegion
      ? destinationBranchIdsFromRegion
      : destinationBranch;

    try {
      const { blob } = await downloadExcel({
        chargeType: ["Charge", "Cash on Delivery"],
        paymentLocation: "Destination",
        status: "Delivered",
        ...(keyword && { keyword }),
        ...(isVendor && { vendorId }),
        account: viewEntity?.entity?.account?._id,
        entity: viewEntity?.entity?._id,
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
        sortedBy,
        sortDirection,
        ...(paymentStatus !== "all" && { paymentStatus }),
        ...(contractorId && { contractorId }),
        ...(driverId && { driverId }),
        ...(finalSourceBranchIds?.length && {
          sourceBranchId: finalSourceBranchIds,
        }),
        ...(finalDestinationBranchIds?.length && {
          destinationBranchId: finalDestinationBranchIds,
        }),
        reportType: "delivery",
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Delivery_Collection_Report_${moment().format(
        "YYYYMMDD"
      )}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
        "No data available for the selected filters. Please adjust your filters."
      );
      console.error("Excel Download Error:", error);
    }
  };

  const columns = [
    {
      name: "Shipment ID",
      selector: (row) => row.shipment?.shipmentId,
      width: "220px",
      cell: (row) => {
        const shipmentIdRaw = row?.shipment?.shipmentId;
        const shipmentDbId = row?.shipment?._id;
        const shipmentStatus = row?.shipment?.shipmentStatus;

        const formattedId = shipmentIdRaw
          ?.replace(/(.{4})/g, "$1-")
          .slice(0, -1);

        return shipmentDbId ? (
          <Box textAlign="center">
            <Link
              to={`/app/crm/shipment/${shipmentDbId}/detail?tab=pricing`}
              title={shipmentIdRaw}
              className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
            >
              {shipmentIdRaw ? (
                <HighlightedText text={formattedId} highlight={keyword} />
              ) : (
                "N/A"
              )}
            </Link>

            {shipmentStatus && (
              <Typography
                sx={(theme) => ({
                  fontSize: "0.75rem",
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[300]
                      : theme.palette.grey[800],
                  mt: 0.5,
                  display: "block",
                })}
              >
                {shipmentStatus}
              </Typography>
            )}
          </Box>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Fee Item",
      selector: (row) => row.feeItem?.name,
      cell: (row) => row.feeItem?.name || "N/A",
    },
    {
      name: "Charge",
      selector: (row) => row.charge,
      cell: (row) => `Rs. ${row.charge?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Payer",
      selector: (row) => row.payer,
      cell: (row) => row.payer || "N/A",
    },
    {
      name: "Payment Location",
      selector: (row) => row.paymentLocation,
      cell: (row) => (
        <div className="text-center">
          <div>{row.shipment?.destinationBranch?.name || "N/A"}</div>
          <div className="text-xs text-gray-500 dark:text-gray-300">
            {row.paymentLocation || "N/A"}
          </div>
        </div>
      ),
    },
    {
      name: "Responsible User",
      selector: (row) => row.responsibleUser,
      cell: (row) => row.responsibleUser.name || "N/A",
    },
    {
      name: "Payment Status",
      selector: (row) => row.paymentStatus,
      cell: (row) => (
        <Chip
          label={row?.paymentStatus || "N/A"}
          variant="outlined"
          sx={{
            backgroundColor: getPaymentStatusStyle(row?.paymentStatus)
              .backgroundColor,
            color: getPaymentStatusStyle(row?.paymentStatus).textColor,
            fontWeight: "bold",
            height: "17px",
            borderRadius: "12px",
            width: "auto",
            padding: "0",
            border: "none",
            "& .MuiChip-label": {
              fontSize: "0.6rem",
              padding: "3px 5px",
              lineHeight: "14px",
            },
          }}
        />
      ),
    },
    ...(paymentStatus !== "Not Paid"
      ? [
        {
          name: "Financed",
          selector: (row) => row.collectStatus,
          cell: (row) => row.collectStatus || "N/A",
        },
      ]
      : []),
    {
      name: "Contractor",
      selector: (row) => row.contractor?.name,
      cell: (row) => row.contractor?.name || "N/A",
    },
    {
      name: "Driver",
      selector: (row) => row.driver?.name,
      cell: (row) => row.driver?.name || "N/A",
    },
    {
      name: "Created At",
      selector: (row) => row.updatedAt,
      cell: (row) => <AppTableDateTime date={row.updatedAt} />,
    },
    {
      name: "Total Days",
      selector: (row) => row?.totalDays,
      cell: (row) => {
        if (!row?.totalDays) return "0 hours";

        const totalHours = row.totalDays;

        if (totalHours >= 24) {
          const days = Math.floor(totalHours / 24);
          const remainingHours = totalHours % 24;

          if (remainingHours > 0) {
            return `${days}d ${remainingHours}h`;
          }
          return `${days} ${days === 1 ? 'day' : 'days'}`;
        } else {
          return `${totalHours} ${totalHours === 1 ? 'hour' : 'hours'}`;
        }
      },
    }
  ];

  useAppMeta("COD Transaction Report");

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Error error={error} />
        <BreadcrumbLayout.Paper>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">COD Transaction Report</Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <BaseSelect
                    label="Time Range"
                    value={timeRange}
                    onChange={(e) => {
                      setTimeRange(e.target.value);
                      if (e.target.value !== "custom") {
                        setCustomFromDate(null);
                        setCustomToDate(null);
                      }
                    }}
                    options={[
                      { label: "Today", value: "today" },
                      { label: "Last 7 Days", value: "last7days" },
                      { label: "One Month", value: "oneMonth" },
                      { label: "Custom", value: "custom" },
                      { label: "All", value: "all" },
                    ]}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.value}
                    size="small"
                    sx={{ minWidth: 160 }}
                  />
                  {timeRange === "custom" && (
                    <Stack direction="row" spacing={1}>
                      <DatePicker
                        label="From Date"
                        value={customFromDate}
                        onChange={(newValue) => {
                          setCustomFromDate(newValue);
                          if (
                            customToDate &&
                            differenceInDays(customToDate, newValue) > 90
                          ) {
                            setCustomToDate(addDays(newValue, 90));
                            setErrorMessage(
                              "Date range adjusted to maximum 90 days."
                            );
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{ width: 160 }}
                          />
                        )}
                        maxDate={customToDate || new Date()}
                        disableFuture
                      />
                      <DatePicker
                        label="To Date"
                        value={customToDate}
                        onChange={(newValue) => {
                          setCustomToDate(newValue);
                          if (
                            customFromDate &&
                            differenceInDays(newValue, customFromDate) > 90
                          ) {
                            setCustomFromDate(addDays(newValue, -90));
                            setErrorMessage(
                              "Date range adjusted to maximum 90 days."
                            );
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{ width: 160 }}
                          />
                        )}
                        minDate={customFromDate}
                        maxDate={new Date()}
                        disableFuture
                      />
                    </Stack>
                  )}
                </Box>
                <TableFilter
                  searchLabel="Shipment ID"
                  disabled={
                    isLoading ||
                    isFetching ||
                    loadingContractors ||
                    loadingDrivers ||
                    isBranchesLoading
                  }
                  initialFilters={
                    contractorId ||
                      driverId ||
                      sourceBranch.length ||
                      destinationBranch.length ||
                      sourceRegion ||
                      destinationRegion
                      ? 1
                      : 0
                  }
                  onClear={clearAllFilters}
                  showDateRange={false}
                >
                  <BaseSelect
                    fullWidth
                    label="Payment Status"
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                    options={[
                      { label: "All", value: "all" },
                      { label: "Paid", value: "Paid" },
                      { label: "Not Paid", value: "Not Paid" },
                    ]}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.value}
                  />

                  <BaseAutocomplete
                    fullWidth
                    multiple
                    label="Source Branch"
                    value={selectedSource}
                    options={mappedBranchList}
                    limitTags={2}
                    onChange={(_, data) => {
                      setSourceBranch(data.map((branch) => branch.value));
                      setSourceRegion("");
                    }}
                    onFocus={() => {
                      if (
                        viewEntity?.entity?.account?._id &&
                        viewEntity?.entity?._id
                      ) {
                        fetchBranches({
                          account: viewEntity.entity.account._id,
                          entity: viewEntity.entity._id,
                        })
                          .unwrap()
                          .catch((error) => {
                            console.error("Failed to fetch branches:", error);
                          });
                      }
                    }}
                    getOptionLabel={(opt) => opt?.label || ""}
                    isOptionEqualToValue={(opt, value) =>
                      opt?.value === value?.value
                    }
                  />

                  <BaseAutocomplete
                    fullWidth
                    multiple
                    label="Destination Branch"
                    value={selectedDestination}
                    limitTags={2}
                    options={mappedBranchList}
                    onChange={(_, data) => {
                      setDestinationBranch(data.map((branch) => branch.value));
                      setDestinationRegion("");
                    }}
                    onFocus={() => {
                      if (
                        viewEntity?.entity?.account?._id &&
                        viewEntity?.entity?._id
                      ) {
                        fetchBranches({
                          account: viewEntity.entity.account._id,
                          entity: viewEntity.entity._id,
                        })
                          .unwrap()
                          .catch((error) => {
                            console.error("Failed to fetch branches:", error);
                          });
                      }
                    }}
                    getOptionLabel={(opt) => opt?.label || ""}
                    isOptionEqualToValue={(opt, value) =>
                      opt?.value === value?.value
                    }
                  />

                  <BaseAutocomplete
                    fullWidth
                    label="Source Province"
                    value={selectedSourceRegion || null}
                    options={mappedRegionList}
                    onChange={handleSourceRegionChange}
                    onFocus={handleFetchRegions}
                    getOptionLabel={(opt) => opt?.label || ""}
                    isOptionEqualToValue={(opt, value) =>
                      opt?.value === value?.value
                    }
                  />

                  <BaseAutocomplete
                    fullWidth
                    label="Destination Province"
                    value={selectedDestinationRegion || null}
                    options={mappedRegionList}
                    onChange={handleDestinationRegionChange}
                    onFocus={handleFetchRegions}
                    getOptionLabel={(opt) => opt?.label || ""}
                    isOptionEqualToValue={(opt, value) =>
                      opt?.value === value?.value
                    }
                  />

                  <BaseSelect
                    fullWidth
                    label="Contractor"
                    value={contractorId}
                    onChange={(e) => setContractorId(e.target.value)}
                    options={[
                      { value: "", label: "All Contractors" },
                      ...(contractors?.map((c) => ({
                        value: c.value,
                        label: c.label,
                      })) || []),
                    ]}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.value}
                    disabled={loadingContractors}
                  />
                  <BaseSelect
                    fullWidth
                    label="Driver"
                    value={driverId}
                    onChange={(e) => setDriverId(e.target.value)}
                    options={[
                      { value: "", label: "All Drivers" },
                      ...(drivers?.map((d) => ({
                        value: d.value,
                        label: d.label,
                      })) || []),
                    ]}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.value}
                    disabled={loadingDrivers || !contractorId}
                  />
                  <BaseSelect
                    fullWidth
                    label="Sort By"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    options={[
                      { label: "Created At (Desc)", value: "createdAt:-1" },
                      { label: "Created At (Asc)", value: "createdAt:1" },
                      { label: "Updated At (Desc)", value: "updatedAt:-1" },
                      { label: "Updated At (Asc)", value: "updatedAt:1" },
                    ]}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.value}
                  />
                </TableFilter>
                <Tooltip title="Download Excel">
                  <span>
                    <IconButton
                      onClick={handleDownloadExcel}
                      color="primary"
                      disabled={
                        isLoading ||
                        isFetching ||
                        isDownloading ||
                        !priceRes?.data?.length
                      }
                    >
                      <Download />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            {isLoading || isFetching || loadingContractors || loadingDrivers || isBranchesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : priceRes?.data?.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No data available for the selected filters.
                </Typography>
              </Box>
            ) : (
              <AppTable
                columns={columns}
                data={priceRes?.data ?? []}
                progressPending={isLoading || isFetching}
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={priceRes?.totalItems ?? 0}
                paginationDefaultPage={currentPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
              />
            )}
          </Box>
        </BreadcrumbLayout.Paper>
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={() => setErrorMessage("")}
        >
          <Alert severity="error" onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default DeliveryCollectionReport;